import React from "react";
import { Switch, Redirect, Route, useLocation } from "react-router-dom";
import loadable from "@loadable/component";
import Footer from "@ugitgud/legos/ui/footer/Footer";
import { RouteStatus } from "@ugg/shared/components/common/RouteStatus";
import { AppRoutes, RouterPaths } from "@ugg/shared/routes/app-routes";
import { RiotIdRedirect } from "@ugg/shared/components/SummonerProfiles/RiotIdRedirect";
import PremiumFAQ from "components/Accounts/PremiumFAQ";
import OpenSearchRedirect from "components/OpenSearchRedirect";
import { Error404 } from "@ugg/shared/components/common/Error404";
import AdWhitelist from "components/Ads/AdWhitelist";
import Login from "components/Accounts/Login";
import Signup from "components/Accounts/Signup";
import VerificationContainer from "components/Pages/VerificationPage/views/VerificationContainer";
import NotFoundUserSummonerProfile from "components/Accounts/NotFoundUserSummonerProfile/views/NotFoundUserSummonerProfile";
import { useBanner, CURRENT_ACTIVE_BANNER } from "@ugitgud/legos/hooks/useBanner";
import { YearInReviewPage } from "components/SummonerProfiles/YearInReview/YearInReviewPage";
import { YearInReviewAuthRouter } from "@ugg/shared/components/YearInReview/YearInReviewAuthRouter";
import { ChampionLeaderboardTab } from "@ugg/shared/components/Leaderboards/ChampionLeaderboardTab";

// Loadable Components
const LandingPageContainer = loadable(() => import("components/Pages/LandingPage/LandingPageContainer"));
const DesktopAppSplashPage = loadable(() => import("components/DesktopAppSplashPage/DesktopAppSplashPage"));
const LeaderboardsPage = loadable(() => import("components/Leaderboards/LeaderboardsPage"), {
  resolveComponent: (components) => components.LeaderboardsPage,
});
const TierListPage = loadable(() => import("components/TierListPage"), {
  resolveComponent: (components) => components.TierListPage,
});
const ChampionsRoutes = loadable(() => import("components/ChampionsRoutes"), {
  resolveComponent: (components) => components.ChampionsRoutes,
});
const ChampionsPage = loadable(() => import("components/Champions/ChampionsPage"), {
  resolveComponent: (components) => components.ChampionsPage,
});
const ClassicChampionsPage = loadable(() => import("components/Champions/ClassicChampionsPage"), {
  resolveComponent: (components) => components.ClassicChampionsPage,
});
const ClassicChampionProfilePage = loadable(() => import("components/Champions/ClassicChampionProfilePage"), {
  resolveComponent: (components) => components.ClassicChampionProfilePage,
});
const SummonerProfile = loadable(() => import("components/SummonerProfiles/SummonerProfile"), {
  resolveComponent: (components) => components.SummonerProfile,
});
const Settings = loadable(() => import("components/Accounts/Settings/Settings"));
const SettingsConnectionsCallback = loadable(() => import("components/Accounts/Settings/SettingsConnectionsCallback"));
const DiscordWebhookCallback = loadable(() => import("components/Accounts/Settings/routes/Premium/DiscordWebhookCallback"));
const RecoverAccount = loadable(() => import("components/Accounts/RecoverAccount/RecoverAccount"));
const PasswordReset = loadable(() => import("components/Accounts/PasswordReset"));
const FAQ = loadable(() => import("components/Pages/FAQ"));
const PrivacyPolicy = loadable(() => import("components/PrivacyPolicy"));
const PrivacyShield = loadable(() => import("components/PrivacyShield"));
const TermsOfService = loadable(() => import("components/TermsOfService"));
const Careers = loadable(() => import("components/Careers/CareersHome"));
const CareersPost = loadable(() => import("components/Careers/CareersPost"));
const PremiumCheckout = loadable(() => import("components/Premium/views/PremiumCheckout"));
const LiveGameSplashContainer = loadable(() => import("components/SummonerProfiles/LiveGame/LiveGameSplash"), {
  resolveComponent: (components) => components.LiveGameSplashContainer,
});
const EmailVerificationPage = loadable(() => import("components/Pages/EmailVerificationPage"));
const ItemsPage = loadable(() => import("components/Items/ItemsPage"), {
  resolveComponent: (components) => components.ItemsPage,
});
const ItemsDirectory = loadable(() => import("components/Items/ItemsDirectory"), {
  resolveComponent: (components) => components.ItemsDirectory,
});
const MultiSearchMain = loadable(() => import("components/MultiSearch/MultiSearchMain"));

const WatchList = loadable(() => import("components/WatchLive/WatchListPage"), {
  resolveComponent: (components) => components.WatchListPage,
});
const YearInReviewPage = loadable(() => import("components/SummonerProfiles/YearInReview/YearInReviewPage"), {
  resolveComponent: (components) => components.YearInReviewPage,
});
const Plus = loadable(() => import("components/Pages/PlusPage"));
const Checkout = loadable(() => import("components/Checkout/CheckoutPage"));
const AuthRoutes = loadable(() => import("components/Accounts/Auth/AuthRoutes"));

// Freestar's Sourcepoint CMP finds this button by id, relabels it per region, and unhides it where a
// privacy law applies. See #pmLink in app.scss.
const privacyManagerLink = (
  <li className="footer-link-item">
    <button id="pmLink" type="button" className="footer-link">
      Privacy Manager
    </button>
  </li>
);

const MainContent = ({ isAccountPage = false }) => {
  const location = useLocation();

  // Whichever banner is live — this drives the content offset, so hardcoding a
  // specific banner's id here meant a seasonal swap silently mis-sized the page.
  const { open } = useBanner(CURRENT_ACTIVE_BANNER);

  const disclaimerText =
    "U.GG Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends.";

  // min-height: calc(100vh - var(--masthead-height) - var(--game-nav-height));
  const contentStyle = open
    ? `calc(100vh - var(--masthead-height) - var(--game-nav-height) - var(--banner-height))`
    : "calc(100vh - var(--masthead-height) - var(--game-nav-height))";

  return (
    <div id="main-content" className="collapsed">
      {/* DO NOT REMOVE 'main-content' id or 'collapsed' class. REQUIRED FOR ADS!!!! */}
      <div id="content-wrapper" style={isAccountPage ? { padding: 0 } : {}}>
        {/* DO NOT REMOVE 'content-wrapper' id. REQUIRED FOR ADS!!!! */}
        <div id="content" style={{ minHeight: contentStyle }}>
          {/* DO NOT REMOVE 'content' id. REQUIRED FOR ADS!!!! */}
          <Switch>
            <Route exact path={AppRoutes.HOME} component={LandingPageContainer} />
            <Route exact path={AppRoutes.UGG_APP_SPLASH} component={DesktopAppSplashPage} />
            <Route exact path={AppRoutes.LIVE_GAMES} component={LiveGameSplashContainer} />
            <Route path={AppRoutes.VERIFY} component={EmailVerificationPage} />
            <Route exact path={AppRoutes.YEAR_IN_REVIEW} component={YearInReviewAuthRouter} />
            <Route path={AppRoutes.PROFILE_YEAR_IN_REVIEW} component={YearInReviewPage} />
            <Route path={AppRoutes.NOT_FOUND_PROFILE} component={NotFoundUserSummonerProfile} />
            <Route path={AppRoutes.PROFILE_ROOT} component={SummonerProfile} />
            <Route
              path={AppRoutes.PROFILE_ROOT_OLD}
              component={RiotIdRedirect}
              /* Must come after AppRoutes.PROFILE_ROOT */
            />
            <Route path={RouterPaths.TIER_LIST} component={TierListPage} />
            {/* Classic champion routes must precede CHAMPION_ROOT so they don't fall through
                to the normal champion profile (which 404s on unknown classic champions). */}
            <Route path={AppRoutes.CLASSIC_CHAMPIONS_HOME} exact component={ClassicChampionsPage} />
            <Route path={AppRoutes.CHAMPION_PROFILE_BUILD_CLASSIC} component={ClassicChampionProfilePage} />
            <Route path={AppRoutes.CHAMPION_HOME} exact component={ChampionsPage} />
            <Route path={AppRoutes.CHAMPION_LEADERBOARD} component={ChampionsPage} />
            <Route path={AppRoutes.CHAMPION_ROOT} component={ChampionsRoutes} />
            <Route path={AppRoutes.ITEM_WIKI} component={ItemsPage} />
            <Route path={AppRoutes.ITEMS_DIRECTORY} component={ItemsDirectory} />
            {/* Leaderboards */}
            <Route path={AppRoutes.LEADERBOARD_CHAMPION_RANKING} component={LeaderboardsPage} />
            <Route path={AppRoutes.LEADERBOARD} component={LeaderboardsPage} />
            <Redirect path={AppRoutes.OLD_LEADERBOARD} to={AppRoutes.LEADERBOARD} />

            <Route path={AppRoutes.TOS} render={(props) => <TermsOfService {...props} />} />
            <Route path={AppRoutes.ADBLOCK_WHITELIST} render={(props) => <AdWhitelist {...props} />} />
            <Route path={AppRoutes.PRIVACY_POLICY} render={(props) => <PrivacyPolicy {...props} />} />
            {/* <Route path={AppRoutes.PRIVACY_SHIELD} render={(props) => <PrivacyShield {...props} />} /> */}
            <Route path={AppRoutes.CAREERS_POST} render={(props) => <CareersPost {...props} />} />
            <Route exact path={AppRoutes.CAREERS} render={(props) => <Careers {...props} />} />
            <Route path={AppRoutes.OPENSEARCH} render={(props) => <OpenSearchRedirect {...props} />} />
            <Route path={AppRoutes.MULTISEARCH} render={(props) => <MultiSearchMain {...props} />} />
            <Route path={AppRoutes.SETTINGS_CONNECTIONS_CALLBACK} component={SettingsConnectionsCallback} />
            <Route path={AppRoutes.SETTINGS_DISCORD_WEBHOOK_CALLBACK} component={DiscordWebhookCallback} />
            <Route path={AppRoutes.SETTINGS} component={Settings} />
            <Route path={AppRoutes.RECOVER_ACCOUNT} component={RecoverAccount} />
            <Route path={AppRoutes.RESET_PASSWORD} component={PasswordReset} />
            <Route path={AppRoutes.FAQ} render={(props) => <FAQ {...props} />} />
            <Route path={AppRoutes.PLUS} component={Plus} />
            <Route path={AppRoutes.LOGIN} render={(props) => <Login {...props} />} />
            <Route path={AppRoutes.SIGNUP} render={(props) => <Signup {...props} />} />
            <Route path={AppRoutes.AUTH} component={AuthRoutes} />
            <Route path={AppRoutes.CHECKOUT} component={Checkout} />
            <Redirect path={AppRoutes.PREMIUM} to={AppRoutes.CHECKOUT} />
            <Route path={AppRoutes.PREMIUM_FAQ} render={(props) => <PremiumFAQ {...props} />} />
            <Route path={AppRoutes.VERIFICATION} render={(props) => <VerificationContainer />} />
            <Route path={AppRoutes.WatchList} render={(props) => <WatchList {...props} />} />
            <RouteStatus statusCode={404} path="*">
              <Error404 />
            </RouteStatus>
          </Switch>
        </div>
        {!isAccountPage && (
          <div className="content-side-padding">
            <Footer disclaimerText={disclaimerText} path={location.pathname} termsLinks={privacyManagerLink} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MainContent;
